import { Component, Inject, NgZone, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationError, Router } from '@angular/router';
import { AuthService } from './modules/auth/auth.service';
import { EchoPermissionChangeService } from './services/echo-permission-change.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { prefetchMapboxWhenIdle } from './shared/utils/mapbox-loader';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	providers: [MessageService, ConfirmationService]
})
export class AppComponent {
	title = 'dme_ng';
	environment = environment;

	constructor(
		public authService: AuthService,
		private echoPermissionService: EchoPermissionChangeService,
		private router: Router,
		private renderer: Renderer2,
		private ngZone: NgZone,
		@Inject(DOCUMENT) private document: Document
	) {
		// On non-prod builds, flag the body so global styles can push the fixed
		// topbar/sidebar down to make room for the environment banner.
		//
		// <body> sits outside the component tree, so no template binding
		// ([class.x], ngClass, HostBinding) can reach it — this has to be
		// imperative. Renderer2 + the DOCUMENT token rather than touching the
		// `document` global directly: it doesn't assume a browser platform and
		// is mockable in tests.
		if (environment.showEnvBanner) {
			this.renderer.addClass(this.document.body, 'env-banner-active');
		}

		const currentUserId = this.authService.getLoggedInUser?.()?.userId;
		if (currentUserId) {
			this.echoPermissionService.listenToUserPermissionChanges(currentUserId);
		}

		this.handleChunkLoadErrors();

		// DOVE-2520 — mapbox-gl no longer ships with the feature modules; fetch it in the
		// background once the page is idle so the first map visit comes from cache. Outside
		// Angular so the prefetch doesn't trigger change detection.
		this.ngZone.runOutsideAngular(() => prefetchMapboxWhenIdle());
	}

	/**
	 * Handles lazy-loaded module failures by reloading the page.
	 * This can happen after deployments when cached JS references old chunk hashes
	 * that no longer exist on the server.
	 */
	private handleChunkLoadErrors(): void {
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationError) {
				const chunkFailedMessage =
					/loading chunk|error loading dynamically imported module|disallowed mime type/i;
				if (chunkFailedMessage.test(event.error?.message || '')) {
					console.warn('Module loading failed, reloading page...', event.error);
					window.location.assign(event.url); // reload to whatever the user is trying to navigate to
				}
			}
		});
	}
}
